import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { left, color, display, boxShadow } from 'styled-system';
import useOutsideClick from 'use-onclickoutside';
import { Box, Card, Button, Text, Flex } from 'rebass';
import { GoX } from 'react-icons/go';
import { BiMenu, BiMessageRoundedError } from "react-icons/bi";
import NavigationSidebar from '../navigation/sidebar';
import { useHasDrawer } from './hooks';
import DrawerHeaderRightLinks from './right-links';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setSidebar } from 'ui/actions';
import { checkIfPublicRoute } from 'app/form/helper';
import store from 'store';
import useHistory from 'app/hooks/history';
import BFYLogo from './assets/logo';
import { CgHome } from "react-icons/cg";
import { CurrentCollectionContext } from 'collections/current-collection-context';
import useCurrentMemberId from 'members/hooks/index';
import MemberAvatar from 'members/avatar/index';
import Tippy from '@tippy.js/react';
import { TooltipWrap } from 'stories/story-detail-header/styled';
import MeDropdown from 'app/navigation/links/me-dropdown';
import useToggle from 'app/hooks/toggle';
import { useMyAllChildJournals } from 'account-dashboard/hooks';

const Drawer = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  width: 270px;
  height: 100vh;
  transition: all 0.3s ease-in-out;
  z-index: 105;
  overflow-y: scroll;

  ${boxShadow};
  ${color};
  ${left};
`;

const DrawerShade = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 104;
  transition: background 0.2s ease-in-out;
  ${color};
  ${display};
`;

const MobileDrawer = ({ children }) => {
  const hasDrawer = useHasDrawer();
  const [on, setOn] = useState(false);
  const [shadeOn, setShadeOn] = useState(false);
  const { replace, push } = useHistory();
  const [isTooltipOpen, setTooltipOpen] = useToggle();
  const wrapper = useRef(null);
  const { journals } = useMyAllChildJournals();
  const hasInitializedJournal = journals.some((journal) => journal?.state === "initialized");

  const { pathname, search } = useLocation();

  const isPublicRoute = checkIfPublicRoute(pathname) || pathname.includes('paywall')

  const dispatch = useDispatch();
  const { currentCollection } = useContext(CurrentCollectionContext);
  const currentMemberId = useCurrentMemberId();

  useOutsideClick(wrapper, () => {
    if (isTooltipOpen)
      setTooltipOpen()
  });

  useEffect(() => {
    setOn(false);
  }, [pathname])

  useEffect(() => {
    if (search === '?showSidebar=true' || search.includes('showSidebar=true')) {
      setOn(true)
      replace(`${pathname}`)
    }
  }, [search, pathname])

  const toggleDrawer = useCallback(() => {
    if (on) {
      store.set('closeDrawer', true);
      setOn(false);
      setTimeout(() => setShadeOn(false), 200);
      dispatch(setSidebar(false))
    } else {
      setOn(true);
      setShadeOn(true);
      dispatch(setSidebar(true))
    }
  }, [on]);

  if (hasDrawer && !isPublicRoute) {
    return (
      <Box>
        <Drawer
          bg="drawerBg"
          left={on ? 0 : -270}
          boxShadow={on ? '0 0 15px rgba(0, 0, 0, 0.3)' : 'none'}
          className="mobile-drawer"
        >
          <Box >
            <Button type="button" variant="transparent" onClick={toggleDrawer}>
              <Text color="#64748B" as={GoX} />
            </Button>
          </Box>
          <NavigationSidebar onCollectionClick={toggleDrawer} />
        </Drawer>
        {on && <DrawerShade
          bg={on ? 'rgba(0,0,0,.7)' : 'transparent'}
          display={shadeOn ? 'block' : 'none'}
          onClick={toggleDrawer}
        />}
        <Card
          bg="white"
          pl={2}
          height="49px"
          css="display: grid; grid-template-columns: 25px 1fr 25px; position: sticky; top: 0; z-index: 4;"
          sx={{
            borderBottom: '1px solid rgba(100, 116, 139, 0.30)',

          }}
        >
          {(isPublicRoute || !hasInitializedJournal) ?
            <Flex width={['160px', '250px', "250px"]} ml="auto">
              <BFYLogo fill="#64748B" />
            </Flex>
            : <Flex>
              <Button minWidth={24} p={0} variant="transparent" type="button" onClick={toggleDrawer}>
                <BiMenu size={24} color="#64748B" />
              </Button>

              {currentCollection?.id && <Flex>
                <Box sx={{ borderLeft: '1px solid #E2E8F0', }} mx="25px" />
                <Button minWidth={24} height="49px" p={0} variant="transparent" type="button" onClick={() => {
                  push(`/child-journals/${currentCollection.id}`)
                }}
                >
                  <CgHome size={24} color="#64748B" />
                </Button>
                <Box mx="25px" sx={{ borderLeft: '1px solid #E2E8F0', }} />
                <Button minWidth={24} height="49px" p={0} variant="transparent" type="button" onClick={() => window.Beacon('open')}>
                  <BiMessageRoundedError size={24} color="#64748B" />
                </Button>
                <Box ml="25px" sx={{ borderLeft: '1px solid #E2E8F0', }} />
              </Flex>
              }
            </Flex>
          }
          <Tippy
            content={<TooltipWrap ref={wrapper}>
              <MeDropdown
                isFromHeader
                journalId={currentCollection?.id}
                handleClick={setTooltipOpen}
                hasInitializedJournal={hasInitializedJournal}
              />
            </TooltipWrap>}
            interactive
            theme="light"
            trigger="manual"
            visible={isTooltipOpen}
            placement="bottom-end"
          >
            <Flex ml="auto" alignItems="center" onClick={setTooltipOpen}>
              <MemberAvatar size={30} id={currentMemberId} />
            </Flex>
          </Tippy>
          <DrawerHeaderRightLinks />
        </Card>
        {children}
      </Box >
    );
  }
  return children;
};

MobileDrawer.propTypes = {
  children: PropTypes.node.isRequired,
};

export default MobileDrawer;
